/**
 * First-party device id (`pf_did`).
 *
 * One random id per browser, sent on every API call as `x-pf-device` so the
 * backend's fraud checks can link sign-ups, checkouts and logins that come
 * from the same device. It is not a fingerprint: nothing about the device is
 * read, it is just a random value we store and send back.
 *
 * Stored twice so either copy can restore the other:
 * - a cookie on `.pocketsflow.com` (path /, 1 year, SameSite=Lax, Secure on
 *   https), shared by the dashboard, checkout and every *.pocketsflow.com
 *   storefront;
 * - localStorage, for hosts where the cookie can't be shared (custom domains
 *   and localhost get a host-only cookie and therefore their own id — fine).
 *
 * Blocked storage (private mode, WebViews with storage off) must never break a
 * request: the id then lives in memory for the page's lifetime.
 */

export const DEVICE_ID_KEY = "pf_did";
export const DEVICE_ID_HEADER = "x-pf-device";

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
const SHARED_COOKIE_DOMAIN = "pocketsflow.com";
/** Ids we mint are 32 hex chars; accept a little range so older ids survive. */
const VALID_ID = /^[A-Za-z0-9_-]{16,64}$/;

let memoryId: string | null = null;

const isValidId = (value: unknown): value is string =>
	typeof value === "string" && VALID_ID.test(value);

/** 128 random bits as hex. Falls back to Math.random only without WebCrypto. */
export function generateDeviceId(): string {
	const bytes = new Uint8Array(16);
	const cryptoApi = globalThis.crypto;
	if (cryptoApi?.getRandomValues) {
		cryptoApi.getRandomValues(bytes);
	} else {
		for (let i = 0; i < bytes.length; i++) {
			bytes[i] = Math.floor(Math.random() * 256);
		}
	}
	return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * `.pocketsflow.com` on pocketsflow.com and its subdomains; undefined (a
 * host-only cookie) everywhere else — custom domains, localhost, previews.
 */
export function deviceCookieDomain(hostname: string): string | undefined {
	const host = hostname.toLowerCase();
	if (
		host === SHARED_COOKIE_DOMAIN ||
		host.endsWith(`.${SHARED_COOKIE_DOMAIN}`)
	) {
		return `.${SHARED_COOKIE_DOMAIN}`;
	}
	return undefined;
}

function readCookie(): string | null {
	try {
		const parts = document.cookie ? document.cookie.split(";") : [];
		for (const part of parts) {
			const [rawName, ...rest] = part.trim().split("=");
			if (rawName === DEVICE_ID_KEY) {
				const value = decodeURIComponent(rest.join("="));
				return isValidId(value) ? value : null;
			}
		}
	} catch {
		// Cookie access can throw in sandboxed iframes.
	}
	return null;
}

function writeCookie(id: string): void {
	try {
		const domain = deviceCookieDomain(window.location.hostname);
		const secure = window.location.protocol === "https:";
		// biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is missing in Safari/Firefox; this must work everywhere
		document.cookie = [
			`${DEVICE_ID_KEY}=${encodeURIComponent(id)}`,
			"path=/",
			`max-age=${ONE_YEAR_SECONDS}`,
			"SameSite=Lax",
			domain ? `domain=${domain}` : "",
			secure ? "Secure" : "",
		]
			.filter(Boolean)
			.join("; ");
	} catch {
		// ignore — localStorage / memory still carry the id
	}
}

function readStorage(): string | null {
	try {
		const value = window.localStorage.getItem(DEVICE_ID_KEY);
		return isValidId(value) ? value : null;
	} catch {
		return null;
	}
}

function writeStorage(id: string): void {
	try {
		window.localStorage.setItem(DEVICE_ID_KEY, id);
	} catch {
		// ignore — storage blocked
	}
}

/**
 * The device id for this browser, created on first call. The cookie wins over
 * localStorage (it is the copy shared across *.pocketsflow.com); whichever copy
 * is missing is re-written so both stay in step.
 */
export function getDeviceId(): string {
	const fromCookie = readCookie();
	const fromStorage = readStorage();
	const id = fromCookie ?? fromStorage ?? memoryId ?? generateDeviceId();
	memoryId = id;
	if (fromCookie !== id) writeCookie(id);
	if (fromStorage !== id) writeStorage(id);
	return id;
}

/** Test-only: forget the in-memory copy. */
export function resetDeviceIdMemory(): void {
	memoryId = null;
}
